import { DateTime } from "luxon";
import { isScheduleMobileWorkingLocation } from "../../../components/Schedule/schedule-event-location.component";
import { isVisitorManagedWorkplaceSchedule } from "../../../components/Schedule/should-show-visitor-schedule-icon.function";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";

const PRIMARY_WORKPLACE_BOOKING_TYPES = new Set<string>([
  BookingType.WORKPLACE,
  BookingType.TEAM,
  BookingType.PLACEZONE,
  BookingType.OPENSPACE
]);

export type Dashboard2BookingBucket =
  | "workplace"
  | "conference"
  | "other"
  | "visitor"
  | "mobile_working";

const BUCKET_PRIORITY: Record<Dashboard2BookingBucket, number> = {
  workplace: 1,
  conference: 2,
  other: 3,
  visitor: 4,
  mobile_working: 5
};

export type PrimaryBookingSelection = {
  primary?: BookingScheduleInterface;
  isFinished?: boolean;
};

export type SelectPrimaryBookingOptions = {
  isToday: boolean;
  now?: DateTime;
};

export function classifyDashboard2BookingBucket(
  schedule: BookingScheduleInterface
): Dashboard2BookingBucket {
  if (isScheduleMobileWorkingLocation(schedule)) {
    return "mobile_working";
  }

  if (schedule.bookingType === BookingType.VISITOR) {
    return "visitor";
  }

  if (isVisitorManagedWorkplaceSchedule(schedule)) {
    return "visitor";
  }

  if (schedule.bookingType === BookingType.CONFERENCEZONE) {
    return "conference";
  }

  if (PRIMARY_WORKPLACE_BOOKING_TYPES.has(schedule.bookingType)) {
    return "workplace";
  }

  return "other";
}

/** Own work-status bookings only — cleared on “Not set”; excludes visitor/external-managed rows. */
export function isDashboard2ClearableWorkStatusBooking(
  schedule: BookingScheduleInterface
): boolean {
  const bucket = classifyDashboard2BookingBucket(schedule);

  return bucket === "workplace" || bucket === "mobile_working";
}

export function bucketPriority(bucket: Dashboard2BookingBucket): number {
  return BUCKET_PRIORITY[bucket];
}

function scheduleStartLocal(schedule: BookingScheduleInterface): DateTime {
  return DateTime.fromISO(schedule.startDate).toLocal();
}

function scheduleEndLocal(schedule: BookingScheduleInterface): DateTime {
  return DateTime.fromISO(schedule.endDate).toLocal();
}

export function isScheduleActiveAtNow(
  schedule: BookingScheduleInterface,
  now: DateTime = DateTime.now()
): boolean {
  const start = scheduleStartLocal(schedule);
  const end = scheduleEndLocal(schedule);

  return start <= now && now < end;
}

export function sortBookingsByStart(
  bookings: readonly BookingScheduleInterface[]
): BookingScheduleInterface[] {
  return [...bookings].sort(
    (left, right) => scheduleStartLocal(left).toMillis() - scheduleStartLocal(right).toMillis()
  );
}

function pickByBucketAndStart(
  candidates: readonly BookingScheduleInterface[]
): BookingScheduleInterface | undefined {
  if (candidates.length === 0) {
    return undefined;
  }

  const sorted = sortBookingsByStart(candidates);

  return sorted.reduce<BookingScheduleInterface | undefined>((best, current) => {
    if (!best) {
      return current;
    }

    const bestPriority = bucketPriority(classifyDashboard2BookingBucket(best));
    const currentPriority = bucketPriority(classifyDashboard2BookingBucket(current));

    if (currentPriority < bestPriority) {
      return current;
    }

    if (currentPriority > bestPriority) {
      return best;
    }

    return scheduleStartLocal(current) < scheduleStartLocal(best) ? current : best;
  }, undefined);
}

function isFutureOnly(bookings: readonly BookingScheduleInterface[], now: DateTime): boolean {
  return bookings.length > 0 && bookings.every(booking => scheduleStartLocal(booking) > now);
}

/**
 * Buckets that establish a planned day status even when only future bookings exist today.
 * Workplace / conference → office; mobile_working → mobile_working.
 * Visitor / parking / fleet / etc. do not (remain unplanned until active or ended).
 */
function isPlannedDayStatusPrimaryBucket(bucket: Dashboard2BookingBucket): boolean {
  return bucket === "workplace" || bucket === "conference" || bucket === "mobile_working";
}

function selectFuturePlannedDayStatusPrimary(
  bookings: readonly BookingScheduleInterface[]
): BookingScheduleInterface | undefined {
  const plannedStatusCandidates = bookings.filter(booking =>
    isPlannedDayStatusPrimaryBucket(classifyDashboard2BookingBucket(booking))
  );

  return pickByBucketAndStart(plannedStatusCandidates);
}

function findLastEndedBooking(
  bookings: readonly BookingScheduleInterface[],
  now: DateTime
): BookingScheduleInterface | undefined {
  const ended = bookings.filter(booking => scheduleEndLocal(booking) <= now);

  if (ended.length === 0) {
    return undefined;
  }

  return ended.reduce((latest, current) =>
    scheduleEndLocal(current) > scheduleEndLocal(latest) ? current : latest
  );
}

function selectSingleBookingToday(
  booking: BookingScheduleInterface,
  now: DateTime
): PrimaryBookingSelection {
  const bucket = classifyDashboard2BookingBucket(booking);

  if (isPlannedDayStatusPrimaryBucket(bucket)) {
    if (scheduleEndLocal(booking) <= now) {
      return { primary: booking, isFinished: true };
    }

    return { primary: booking };
  }

  if (isScheduleActiveAtNow(booking, now)) {
    return { primary: booking };
  }

  if (scheduleEndLocal(booking) <= now) {
    return { primary: booking, isFinished: true };
  }

  return {};
}

export function selectPrimaryBookingForDay(
  bookings: readonly BookingScheduleInterface[],
  options: SelectPrimaryBookingOptions
): PrimaryBookingSelection {
  const now = options.now ?? DateTime.now();
  const sorted = sortBookingsByStart(bookings);

  if (sorted.length === 0) {
    return {};
  }

  if (sorted.length === 1) {
    const only = sorted[0]!;

    if (options.isToday) {
      return selectSingleBookingToday(only, now);
    }

    return { primary: only };
  }

  if (options.isToday) {
    const activeNow = sorted.filter(booking => isScheduleActiveAtNow(booking, now));
    const activePrimary = pickByBucketAndStart(activeNow);

    if (activePrimary) {
      return { primary: activePrimary };
    }

    const lastEnded = findLastEndedBooking(sorted, now);

    if (lastEnded) {
      return { primary: lastEnded, isFinished: true };
    }

    if (isFutureOnly(sorted, now)) {
      const futurePlannedPrimary = selectFuturePlannedDayStatusPrimary(sorted);

      return futurePlannedPrimary ? { primary: futurePlannedPrimary } : {};
    }

    return {};
  }

  const primary = pickByBucketAndStart(sorted);

  return primary ? { primary } : {};
}

export function isOwnWorkplacePrimaryForCheckIn(
  schedule: BookingScheduleInterface | undefined
): boolean {
  if (!schedule) {
    return false;
  }

  return classifyDashboard2BookingBucket(schedule) === "workplace";
}
